import { Users, Shield, Heart, Sparkles } from 'lucide-react';

const WhyDira = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-forest-50/30 to-golden-50/20 relative overflow-hidden">
      {/* Animated organic background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-coral-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-40 -right-20 w-96 h-96 bg-golden-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-20 left-1/3 w-96 h-96 bg-forest-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
          <div className="text-center mb-16 animate-fade-in-up">
            <h1 className="text-5xl md:text-6xl font-bold text-forest-800 mb-6">
              Why Dira?
            </h1>
            <p className="text-xl text-forest-700 max-w-3xl mx-auto leading-relaxed">
              Because skills alone don't change the script. We're here to help children—and adults—rewrite the internal story that shapes everything.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-16">
          
          {/* The Internal Experiment */}
          <section className="animate-fade-in-up animation-delay-200">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-lg border border-forest-100">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-8 h-8 text-forest-600" />
                <h2 className="text-3xl font-bold text-forest-800">The Internal Experiment</h2>
              </div>
              <p className="text-lg text-forest-700 mb-6 leading-relaxed">
                Kids don't just learn mindfulness or "use their words" in a vacuum. They're constantly running an internal experiment:
              </p>
              <ul className="space-y-4 text-lg text-forest-700">
                <li className="flex items-start gap-3">
                  <span className="text-2xl">•</span>
                  <span>When I show my feelings, do people really see me?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">•</span>
                  <span>When I struggle or mess up, do I still matter here?</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">•</span>
                  <span>When I try something brave, is it safe to be imperfect and unsure?</span>
                </li>
              </ul>
              <p className="text-lg text-forest-700 mt-6 leading-relaxed">
                Many adults are running the same experiment—at work, in relationships, in parenting—shaped by years of perfectionism and feeling like they only matter when they "get it right."
              </p>
            </div>
          </section>

          {/* When Skills Become Performance */}
          <section className="animate-fade-in-up animation-delay-400">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-lg border border-coral-100">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-8 h-8 text-coral-600" />
                <h2 className="text-3xl font-bold text-forest-800">When Skills Become Performance</h2>
              </div>
              <p className="text-lg text-forest-700 mb-8 leading-relaxed">
                If we only teach skills—breathe, name your feeling, share, be grateful—without changing the relational messages around them, three things tend to happen:
              </p>
              
              <div className="space-y-6">
                <div className="border-l-4 border-coral-400 pl-6 py-4">
                  <h3 className="text-xl font-semibold text-forest-800 mb-2">1. Skills become performance.</h3>
                  <p className="text-forest-700 leading-relaxed">
                    The child (and the adult) learns, "I'm good when I self‑regulate quickly, when I say the right feeling word, when I stay calm—then people are pleased." That reinforces conditional worth: I matter when I manage myself well for you.
                  </p>
                </div>
                
                <div className="border-l-4 border-golden-400 pl-6 py-4">
                  <h3 className="text-xl font-semibold text-forest-800 mb-2">2. Big feelings become a private burden.</h3>
                  <p className="text-forest-700 leading-relaxed">
                    If skills are used mainly to "get everyone under control," kids and adults may get better at hiding or rushing their feelings, not actually feeling safer. Inside, they're still running: My real feelings are too much; my job is to stay easy and pleasant.
                  </p>
                </div>
                
                <div className="border-l-4 border-forest-400 pl-6 py-4">
                  <h3 className="text-xl font-semibold text-forest-800 mb-2">3. The core wound—"I don't really matter"—stays untouched.</h3>
                  <p className="text-forest-700 leading-relaxed">
                    You can have a child or adult who can breathe, label emotions, say thank you, and "do the right things"…and still fundamentally believes: No one really sees me. I only matter when I'm useful, calm, or impressive.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* The Dira Approach */}
          <section className="animate-fade-in-up animation-delay-600">
            <div className="bg-gradient-to-br from-forest-50 to-golden-50 rounded-3xl p-8 md:p-12 shadow-lg border border-golden-200">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-8 h-8 text-golden-600" />
                <h2 className="text-3xl font-bold text-forest-800">Dira is Built to Go After That Deeper Layer</h2>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white/60 rounded-2xl p-6">
                  <p className="text-lg text-forest-800 leading-relaxed">
                    <span className="font-semibold">The stories and characters</span> make children's inner worlds visible and honored.
                  </p>
                </div>
                
                <div className="bg-white/60 rounded-2xl p-6">
                  <p className="text-lg text-forest-800 leading-relaxed">
                    <span className="font-semibold">The adult guides</span> coach parents, caregivers, and teachers to respond in ways that say, over and over: I see you. Your feelings are real. You are cared for when you're scared, messy, trying—not just when you're easy or successful.
                  </p>
                </div>
                
                <div className="bg-white/60 rounded-2xl p-6">
                  <p className="text-lg text-forest-800 leading-relaxed">
                    <span className="font-semibold">Along the way</span>, adults are invited to practice the same skills and new responses themselves, and to hear a different message in their own lives: you don't have to be a perfect parent, teacher, or colleague to matter.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* A Different Outcome */}
          <section className="animate-fade-in-up animation-delay-800">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-lg border border-forest-100">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-8 h-8 text-golden-600" />
                <h2 className="text-3xl font-bold text-forest-800">A Different Outcome</h2>
              </div>
              <p className="text-lg text-forest-700 mb-6 leading-relaxed">
                That's not just nicer SEL; it's a different outcome. We're aiming for kids—and the adults around them—who don't just know how to cope, but who quietly carry this internal script into adolescence and adulthood:
              </p>
              
              <blockquote className="bg-golden-50 border-l-4 border-golden-400 p-8 rounded-r-2xl my-8">
                <p className="text-2xl font-semibold text-forest-800 leading-relaxed italic">
                  "I matter to people. My inner life counts. I don't have to earn care by being perfect, calm, or impressive."
                </p>
              </blockquote>
              
              <p className="text-lg text-forest-700 leading-relaxed">
                That script changes how they handle shame, failure, pressure, and relationships—the whole arc from trauma risk to human success.
              </p>
            </div>
          </section>

          {/* Closing Statement */}
          <section className="animate-fade-in-up animation-delay-1000">
            <div className="text-center rounded-3xl p-12 shadow-xl" style={{ background: "linear-gradient(135deg, #1a4731 0%, #0f3320 100%)" }}>
              <h2 className="text-4xl font-bold text-white mb-4">
                That's Why Dira Exists.
              </h2>
              <p className="text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
                To help children and adults alike rewrite the internal story that shapes everything—from how they see themselves to how they show up in the world.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WhyDira;
